import { apiRequest } from '@/shared/lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface VersionResponse {
  version: string;
  built_at: string | null;
}

/**
 * Rodapé do menu do usuário com versões do APP/API + status do servidor + relógio.
 * Inspirado em padrão de SaaS B2B (ex.: AtenderBem). Útil pro suporte saber
 * exatamente que versão o cliente está usando ao reportar bug.
 */
export function VersionFooter() {
  const [now, setNow] = useState(() => new Date());

  // Polling /version a cada 60s — também serve como heartbeat de "servidor disponível".
  // `retry: false` pra erro virar visível imediato (sem 3 tentativas mascarando).
  const apiVersionQuery = useQuery({
    queryKey: ['system-version'],
    queryFn: () => apiRequest<VersionResponse>('/version', { skipAuth: true }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: false,
  });

  // Relógio com tick de 1s. Limpo no unmount.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const formattedNow = now.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const apiOk = apiVersionQuery.isSuccess && !apiVersionQuery.isError;

  return (
    <div className="border-t border-n-200 bg-n-50 px-4 py-3 text-center text-[11px]">
      <div className="mb-1 flex flex-wrap items-center justify-center gap-1.5">
        <span
          className="inline-flex items-center rounded-md bg-n-700 px-2 py-0.5 font-mono text-[10px] font-semibold text-white"
          title={`Build: ${__APP_BUILT_AT__}`}
        >
          APP: v{__APP_VERSION__}
        </span>
        <span className="inline-flex items-center rounded-md bg-brand-500 px-2 py-0.5 font-mono text-[10px] font-semibold text-white">
          API v{apiVersionQuery.data?.version ?? '...'}
        </span>
      </div>
      <p className={apiOk ? 'text-success-600' : 'text-danger-600'}>
        {apiOk ? 'Servidor disponível!' : 'Sem conexão com o servidor'}
      </p>
      <p className="mt-0.5 font-mono text-n-500">{formattedNow}</p>
    </div>
  );
}
